<template>
  <v-flex class="list-main">
    <v-container style="margin-bottom: 0;">
      <h1
        v-if="type === 'default' || type === 'share' || type === 'public'"
        class="font-weight-bold mb-2 mt-2 d-flex align-center"
      >
        <v-icon class="mr-3" large>mdi-notebook</v-icon>
        <span v-if="type === 'default'">Notebooks</span>
        <span v-else-if="type === 'share'">Shared notebooks</span>
        <span v-else>Public notebooks</span>
      </h1>
      <h1
        v-else-if="type === 'archives'"
        class="font-weight-bold mb-12 mt-2 d-flex align-center"
      >
        <v-icon class="mr-3" large>mdi-delete</v-icon>
        <span>Archives</span>
      </h1>
      <h1 v-else><!-- Nothing --></h1>
      <v-row v-if="type === 'default'" class="ml-2">
        <v-col md="4" class="d-flex align-center">
          <v-btn color="primary" dark @click="page_to('/notebooks/new')">
            + New
          </v-btn>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="3" class="font-weight-bold mt-6">
          <v-icon class="mr-3 ml-2">mdi-tag</v-icon>
          Search by tags...
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="conditions.cellLabels.selected"
            :items="conditions.cellLabels.items"
            chips
            label="Cell labels..."
            multiple
            item-text="Title"
          ></v-select>
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="conditions.experimentLabels.selected"
            :items="conditions.experimentLabels.items"
            chips
            label="Experiments..."
            multiple
            item-text="Title"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="displayNotebooks"
            :items-per-page="25"
            class="elevation-1 ml-3"
            :mobile-breakpoint="MOBILE_BREAK_POINT"
            :loading="!loaded"
            :footer-props="{
              'items-per-page-options': [25, 50, 100],
              showFirstLastPage: true,
            }"
          >
            <template v-slot:item.title="{item}">
              <template
                v-if="
                  type === 'default' || type === 'share' || type === 'public'
                "
              >
                <v-btn
                  text
                  color="blue"
                  @click="page_to('/notebooks/' + item.id)"
                  class="text-none title-text"
                  style="padding: 0px; ustify-content: left;"
                >
                  <v-icon class="mr-3">mdi-file-outline</v-icon>
                  {{ trimText(item.title, 15) }}
                </v-btn>
              </template>
              <template v-else>
                <v-icon class="mr-3">mdi-file-outline</v-icon>
                {{ trimText(item.title, 15) }}
              </template>
            </template>
            <template v-slot:item.cellLabels.value="{item}">
              <template v-for="(val, index) in item.cellLabels.value">
                <v-chip
                  :key="index"
                  :color="'#' + val.Color"
                  :dark="isTextColorDark('#' + item.Color)"
                  class="ma-1"
                >
                  {{ trimText(val.Title, 10) }}
                </v-chip>
              </template>
            </template>
            <template v-slot:item.experimentLabels.value="{item}">
              <template v-for="(val, index) in item.experimentLabels.value">
                <v-chip
                  :key="index"
                  :color="'#' + val.Color"
                  :dark="isTextColorDark('#' + item.Color)"
                  class="ma-1"
                >
                  {{ trimText(val.Title, 10) }}
                </v-chip>
              </template>
            </template>
            <template v-slot:item.owner="{item}">
              <v-btn
                text
                color="blue"
                class="text-none title-text"
                style="padding: 0px; ustify-content: left;"
                @click.stop="showProfileDialog(item.owner)"
                :loading="item.owner.loading"
              >
                <v-icon class="mr-3">mdi-account</v-icon>
                {{ trimText(item.owner.id, 8) }}
              </v-btn>
            </template>
            <template v-slot:item.action="{item}" style="padding: 0px;">
              <v-btn
                v-if="type === 'archives'"
                text
                color="blue"
                @click.stop="restore(item)"
                class="text-none"
                :loading="item.loading"
                style="padding: 0px;"
              >
                Move to
                <br />
                database
              </v-btn>
            </template>
            <template v-slot:item.shared="{item}">
              <div class="text-center">
                <v-icon v-if="item.shared">mdi-link-variant</v-icon>
              </div>
            </template>
            <template v-slot:item.publicAccess="{item}">
              <div class="text-center">
                <v-icon v-if="item.publicAccess">mdi-earth</v-icon>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- profile dialog -->
      <template v-show="profileDialog">
        <profile-dialog
          :profile-dialog="profileDialog"
          :profile="currentProfile"
          @setProfileDialog="setProfileDialog"
        ></profile-dialog>
      </template>
    </v-container>
  </v-flex>
</template>

<script>
import {mapState, mapMutations} from 'vuex'
import moment from 'moment'
import Vue from 'vue'
import ProfileDialog from '@/components/account/ProfileDialog.vue'

import 'viewerjs/dist/viewer.css'
import Viewer from 'v-viewer'
Vue.use(Viewer)

export default {
  title: 'notebooks list',
  components: {
    ProfileDialog,
  },
  props: {
    type: {
      type: String,
      validator(val) {
        return ['default', 'archives', 'share', 'public'].includes(val)
      },
      default: 'default',
    },
  },
  data: () => ({
    notebooks: [],
    loaded: false,
    initing: false,
    profileDialog: false,
    currentProfile: null,
    conditions: {
      mouseLines: {
        category: 'strain',
        items: null,
      },
      cellLabels: {
        category: 'marker',
        items: null,
        selected: [],
      },
      experimentLabels: {
        category: 'experiment',
        items: null,
        selected: [],
      },
    },
    headers: [
      {text: 'Title', value: 'title', width: '300px'},
      {
        text: 'Created at',
        value: 'created_at',
        width: '120px',
      },
      {
        text: 'Cell labels',
        value: 'cellLabels.value',
        width: '120px',
      },
      {
        text: 'Experiments',
        value: 'experimentLabels.value',
        width: '120px',
      },
      {text: 'Shared?', value: 'shared', width: '100px'},
      {text: 'Public?', value: 'publicAccess', width: '100px'},
      {text: 'Owner', value: 'owner', width: '200px'},
      {text: '', value: 'action', sortable: false, width: '20px'},
    ],
  }),
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    displayNotebooks: function() {
      const displayNotebooks = this.notebooks.concat()
      // TODO: implementation

      // // Display all.
      // if (
      //   this.conditions.experimentLabels.selected.length == 0 &&
      //   this.conditions.cellLabels.selected.length == 0
      // ) {
      //   sortedBrains = this.brains.concat()
      // }
      // // Search by and-condition if both of the conditions are specified.
      // if (this.conditions.experimentLabels.selected.length > 0 &&
      //   this.conditions.cellLabels.selected.length > 0) {
      //   this.brains.forEach((brain) => {
      //     let checkExperimentLabels; let checkCellLabels = false

      //     for (const item of brain.experimentLabels.value) {
      //       if (this.conditions.experimentLabels.selected.includes(item.Title)) {
      //         checkExperimentLabels = true
      //         break
      //       }
      //     }
      //     for (const item of brain.cellLabels.value) {
      //       if (this.conditions.cellLabels.selected.includes(item.Title)) {
      //         checkCellLabels = true
      //         break
      //       }
      //     }
      //     if (checkExperimentLabels && checkCellLabels) {
      //       sortedBrains.push(brain)
      //     }
      //   })
      // } else {
      //   this.brains.forEach((brain) => {
      //     //  Search by or-condition if one of the conditions is specified.
      //     for (const item of brain.experimentLabels.value) {
      //       if (this.conditions.experimentLabels.selected.includes(item.Title)) {
      //         sortedBrains.push(brain)
      //         break
      //       }
      //     }
      //     for (const item of brain.cellLabels.value) {
      //       if (this.conditions.cellLabels.selected.includes(item.Title)) {
      //         sortedBrains.push(brain)
      //         break
      //       }
      //     }
      //   })
      // }
      return displayNotebooks
    },
  },
  methods: {
    ...mapMutations({
      setContent: 'pages/setContent',
    }),
    async initNotebooks() {
      while (true) {
        if (!this.initing) {
          break
        }
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
      Object.assign(this.$data, this.$options.data())
      this.initing = true
      this.$_processHeaderByType()
      // Set conditions
      for (const condition of Object.values(this.conditions)) {
        if (condition.items) {
          continue
        }
        const list = await this.getMyTags(condition.category)
        if (!list) {
          break
        }
        condition.items = list.Items
      }

      const notebooks = await this.getMyNotebooks(this.type)
      if (!notebooks) {
        this.initing = false
        return
      }
      for (const notebook of notebooks) {
        this.notebooks.push(this.$_createNotebookObjectForDisplay(notebook))
      }
      this.notebooks = this.sortByCreatedAt(this.notebooks, false)
      this.initing = false
      this.loaded = true
    },
    async showProfileDialog(owner) {
      if (!owner.loaded) {
        owner.loading = true
        owner.info = await this.getAccountById(owner.id)
        owner.loading = false
        owner.loaded = true
      }
      if (owner.info) {
        this.currentProfile = owner.info
        this.profileDialog = true
      }
    },
    setProfileDialog(isShow) {
      this.profileDialog = isShow
    },
    async restore(notebook) {
      notebook.loading = true
      const result = await this.restoreNotebook(notebook.id)
      if (!result) {
        // Do nothing and continue the process.
      }
      this.initNotebooks()
    },
    $_createNotebookObjectForDisplay(notebook) {
      return {
        id: notebook.ItemId,
        title: notebook.Title,
        created_at_origin: notebook.CreatedAt,
        created_at: moment(notebook.CreatedAt).format('YYYY/MM/DD'),
        updated_at: moment(notebook.UpdatedAt).format('YYYY/MM/DD'),
        cellLabels: {
          value: [],
          dispValue: [],
          editable: false,
          updatable: true,
          loading: false,
        },
        experimentLabels: {
          value: [],
          dispValue: [],
          editable: false,
          updatable: true,
          loading: false,
        },
        shared: notebook.Shared,
        loading: false,
        publicAccess: notebook.PublicAccess,
        owner: {
          id: notebook.OwnerId,
          info: null,
          loaded: false,
          loading: false,
        },
      }
    },
    $_processHeaderByType() {
      switch (this.type) {
        case 'default':
          this.headers = this.headers.filter(
            (header) => header.value !== 'owner' && header.value !== 'action'
          )
          break
        case 'share':
        case 'public':
          this.headers = this.headers.filter(
            (header) =>
              header.value !== 'shared' &&
              header.value !== 'publicAccess' &&
              header.value !== 'action'
          )
          break
        case 'archives':
          this.headers = this.headers.filter(
            (header) => header.value !== 'owner'
          )
          break
        default:
      }
    },
  },
  mounted() {
    this.initNotebooks()
  },
}
</script>

<style scoped>
.list-main >>> .v-data-table-header {
  background-color: WhiteSmoke;
}
.list-main >>> .v-data-table-header th {
  border-bottom: gray 1px solid !important;
}
.list-main >>> .v-data-table-header-mobile {
  display: none;
}
.list-main >>> .title-text > .v-btn__content {
  justify-content: left;
}
</style>
